import { calendarDate } from '../utility/dateLocale';
import {
  Accordion,
  Group,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import type {
  FunctionalityVerificationFormData,
  QualityReleaseFormData,
  InventoryInspectionForms as InventoryInspectionFormsData,
} from '../utility/inventoryInspection';
import { okOptions, yesNoOptions } from '../utility/inventoryInspection';

type InventoryInspectionFormsProps = {
  forms: InventoryInspectionFormsData;
  showQualityRelease: boolean;
  showFunctionalityVerification: boolean;
  onChange: (forms: InventoryInspectionFormsData) => void;
};

function parseDate(value: string | null | undefined): Date | null {
  return calendarDate(value);
}

function formatDate(value: Date | null): string | null {
  if (!value) {
    return null;
  }
  return value.toISOString().split('T')[0];
}

function updateQualityRelease(
  forms: InventoryInspectionFormsData,
  patch: Partial<QualityReleaseFormData>,
): InventoryInspectionFormsData {
  return {
    ...forms,
    quality_release: {
      ...(forms.quality_release ?? {}),
      ...patch,
    },
  };
}

function updateFunctionality(
  forms: InventoryInspectionFormsData,
  patch: Partial<FunctionalityVerificationFormData>,
): InventoryInspectionFormsData {
  return {
    ...forms,
    functionality_verification: {
      ...(forms.functionality_verification ?? {}),
      ...patch,
    },
  };
}

export default function InventoryInspectionForms({
  forms,
  showQualityRelease,
  showFunctionalityVerification,
  onChange,
}: InventoryInspectionFormsProps) {
  const { t } = useTranslation();
  const quality = forms.quality_release ?? {};
  const functionality = forms.functionality_verification ?? {};

  if (!showQualityRelease && !showFunctionalityVerification) {
    return null;
  }

  return (
    <Accordion variant="separated" multiple defaultValue={[]}>
      {showQualityRelease ? (
        <Accordion.Item value="quality-release">
          <Accordion.Control>{t('inventory.inspection.qualityReleaseTitle')}</Accordion.Control>
          <Accordion.Panel>
            <Group grow mb="sm">
              <Select
                label={t('inventory.inspection.packingListCheck')}
                data={okOptions}
                value={quality.packing_list_check ?? null}
                onChange={(value) =>
                  onChange(updateQualityRelease(forms, { packing_list_check: value ?? '' }))
                }
              />
              <Select
                label={t('inventory.inspection.originLabel')}
                data={yesNoOptions}
                value={quality.origin_label ?? null}
                onChange={(value) =>
                  onChange(updateQualityRelease(forms, { origin_label: value ?? '' }))
                }
              />
              <Select
                label={t('inventory.inspection.packagingCondition')}
                data={yesNoOptions}
                value={quality.packaging_condition ?? null}
                onChange={(value) =>
                  onChange(updateQualityRelease(forms, { packaging_condition: value ?? '' }))
                }
              />
              <Select
                label={t('inventory.inspection.quantitiesCorrect')}
                data={yesNoOptions}
                value={quality.quantities_correct ?? null}
                onChange={(value) =>
                  onChange(updateQualityRelease(forms, { quantities_correct: value ?? '' }))
                }
              />
            </Group>
            <Textarea
              label={t('inventory.inspection.receptionObservations')}
              value={quality.reception_observations ?? ''}
              onChange={(e) =>
                onChange(
                  updateQualityRelease(forms, {
                    reception_observations: e.currentTarget.value,
                  }),
                )
              }
              mb="sm"
            />
            <Textarea
              label={t('inventory.inspection.quarantineReason')}
              value={quality.quarantine_reason ?? ''}
              onChange={(e) =>
                onChange(
                  updateQualityRelease(forms, { quarantine_reason: e.currentTarget.value }),
                )
              }
              mb="sm"
            />
            <Group grow mb="sm">
              <Select
                label={t('inventory.inspection.supplierRectification')}
                data={yesNoOptions}
                value={quality.supplier_rectification ?? null}
                onChange={(value) =>
                  onChange(
                    updateQualityRelease(forms, { supplier_rectification: value ?? '' }),
                  )
                }
              />
              <DateInput
                label={t('inventory.inspection.supplierRectificationDate')}
                value={parseDate(quality.supplier_rectification_date)}
                onChange={(value) =>
                  onChange(
                    updateQualityRelease(forms, {
                      supplier_rectification_date: formatDate(value),
                    }),
                  )
                }
              />
              <Select
                label={t('inventory.inspection.nonConformityStarted')}
                data={yesNoOptions}
                value={quality.non_conformity_started ?? null}
                onChange={(value) =>
                  onChange(
                    updateQualityRelease(forms, { non_conformity_started: value ?? '' }),
                  )
                }
              />
              <TextInput
                label={t('inventory.inspection.nonConformityNumber')}
                value={quality.non_conformity_number ?? ''}
                onChange={(e) =>
                  onChange(
                    updateQualityRelease(forms, {
                      non_conformity_number: e.currentTarget.value,
                    }),
                  )
                }
              />
            </Group>
            <DateInput
              label={t('inventory.inspection.nonConformityDate')}
              value={parseDate(quality.non_conformity_date)}
              onChange={(value) =>
                onChange(
                  updateQualityRelease(forms, { non_conformity_date: formatDate(value) }),
                )
              }
              mb="sm"
            />
            <Group grow mb="sm">
              <Select
                label={t('inventory.inspection.visualControl')}
                data={okOptions}
                value={quality.visual_control ?? null}
                onChange={(value) =>
                  onChange(updateQualityRelease(forms, { visual_control: value ?? '' }))
                }
              />
              <Select
                label={t('inventory.inspection.importerLabel')}
                data={yesNoOptions}
                value={quality.importer_label ?? null}
                onChange={(value) =>
                  onChange(updateQualityRelease(forms, { importer_label: value ?? '' }))
                }
              />
              <Select
                label={t('inventory.inspection.additionalTestRequired')}
                data={yesNoOptions}
                value={quality.additional_test_required ?? null}
                onChange={(value) =>
                  onChange(
                    updateQualityRelease(forms, { additional_test_required: value ?? '' }),
                  )
                }
              />
              <Select
                label={t('inventory.inspection.expiryInTime')}
                data={yesNoOptions}
                value={quality.expiry_in_time ?? null}
                onChange={(value) =>
                  onChange(updateQualityRelease(forms, { expiry_in_time: value ?? '' }))
                }
              />
            </Group>
            <Textarea
              label={t('inventory.inspection.finalInspectionObservations')}
              value={quality.final_inspection_observations ?? ''}
              onChange={(e) =>
                onChange(
                  updateQualityRelease(forms, {
                    final_inspection_observations: e.currentTarget.value,
                  }),
                )
              }
              mb="sm"
            />
            <Group grow mb="sm">
              <Select label={t('inventory.inspection.labelImporterName')} data={okOptions} value={quality.labeling_importer_name ?? null} onChange={(value) => onChange(updateQualityRelease(forms, { labeling_importer_name: value ?? '' }))} />
              <Select label={t('inventory.inspection.labelLegibility')} data={okOptions} value={quality.labeling_legibility ?? null} onChange={(value) => onChange(updateQualityRelease(forms, { labeling_legibility: value ?? '' }))} />
              <Select label={t('inventory.inspection.labelProductName')} data={okOptions} value={quality.labeling_product_name ?? null} onChange={(value) => onChange(updateQualityRelease(forms, { labeling_product_name: value ?? '' }))} />
              <Select label={t('inventory.inspection.labelDtData')} data={okOptions} value={quality.labeling_dt_data ?? null} onChange={(value) => onChange(updateQualityRelease(forms, { labeling_dt_data: value ?? '' }))} />
              <Select label={t('inventory.inspection.labelPmNumber')} data={okOptions} value={quality.labeling_pm_number ?? null} onChange={(value) => onChange(updateQualityRelease(forms, { labeling_pm_number: value ?? '' }))} />
            </Group>
            <Textarea
              label={t('inventory.inspection.labelingObservations')}
              value={quality.labeling_observations ?? ''}
              onChange={(e) =>
                onChange(
                  updateQualityRelease(forms, {
                    labeling_observations: e.currentTarget.value,
                  }),
                )
              }
              mb="sm"
            />
            <Group grow mb="sm">
              <TextInput
                label={t('inventory.inspection.qcSignature')}
                value={quality.qc_signature ?? ''}
                onChange={(e) =>
                  onChange(updateQualityRelease(forms, { qc_signature: e.currentTarget.value }))
                }
              />
              <TextInput
                label={t('inventory.inspection.releaseSignature')}
                value={quality.release_signature ?? ''}
                onChange={(e) =>
                  onChange(
                    updateQualityRelease(forms, { release_signature: e.currentTarget.value }),
                  )
                }
              />
            </Group>
          </Accordion.Panel>
        </Accordion.Item>
      ) : null}

      {showFunctionalityVerification ? (
        <Accordion.Item value="functionality-verification">
          <Accordion.Control>{t('inventory.inspection.functionalityVerificationTitle')}</Accordion.Control>
          <Accordion.Panel>
            <Textarea
              label={t('inventory.inspection.purchaseOrderComparisonNotes')}
              value={functionality.purchase_order_notes ?? ''}
              onChange={(e) =>
                onChange(
                  updateFunctionality(forms, {
                    purchase_order_notes: e.currentTarget.value,
                  }),
                )
              }
              mb="sm"
            />
            <Group grow mb="sm">
              <Select label={t('inventory.inspection.componentsYes')} data={yesNoOptions} value={functionality.components_yes ?? null} onChange={(value) => onChange(updateFunctionality(forms, { components_yes: value ?? '' }))} />
              <Select label={t('inventory.inspection.componentsNo')} data={yesNoOptions} value={functionality.components_no ?? null} onChange={(value) => onChange(updateFunctionality(forms, { components_no: value ?? '' }))} />
            </Group>
            <Textarea label={t('inventory.inspection.componentsObservations')} value={functionality.components_observations ?? ''} onChange={(e) => onChange(updateFunctionality(forms, { components_observations: e.currentTarget.value }))} mb="sm" />
            <Group grow mb="sm">
              <Select label={t('inventory.inspection.accessoriesYes')} data={yesNoOptions} value={functionality.accessories_yes ?? null} onChange={(value) => onChange(updateFunctionality(forms, { accessories_yes: value ?? '' }))} />
              <Select label={t('inventory.inspection.accessoriesNo')} data={yesNoOptions} value={functionality.accessories_no ?? null} onChange={(value) => onChange(updateFunctionality(forms, { accessories_no: value ?? '' }))} />
            </Group>
            <Textarea label={t('inventory.inspection.accessoriesObservations')} value={functionality.accessories_observations ?? ''} onChange={(e) => onChange(updateFunctionality(forms, { accessories_observations: e.currentTarget.value }))} mb="sm" />
            <Group grow mb="sm">
              <Select label={t('inventory.inspection.softwareLicenseYes')} data={yesNoOptions} value={functionality.software_license_yes ?? null} onChange={(value) => onChange(updateFunctionality(forms, { software_license_yes: value ?? '' }))} />
              <Select label={t('inventory.inspection.softwareLicenseNo')} data={yesNoOptions} value={functionality.software_license_no ?? null} onChange={(value) => onChange(updateFunctionality(forms, { software_license_no: value ?? '' }))} />
            </Group>
            <Textarea label={t('inventory.inspection.softwareLicenseObservations')} value={functionality.software_license_observations ?? ''} onChange={(e) => onChange(updateFunctionality(forms, { software_license_observations: e.currentTarget.value }))} mb="sm" />
            <Group grow mb="sm">
              <Select label={t('inventory.inspection.softwareVersionYes')} data={yesNoOptions} value={functionality.software_version_yes ?? null} onChange={(value) => onChange(updateFunctionality(forms, { software_version_yes: value ?? '' }))} />
              <Select label={t('inventory.inspection.softwareVersionNo')} data={yesNoOptions} value={functionality.software_version_no ?? null} onChange={(value) => onChange(updateFunctionality(forms, { software_version_no: value ?? '' }))} />
            </Group>
            <Textarea label={t('inventory.inspection.softwareVersionObservations')} value={functionality.software_version_observations ?? ''} onChange={(e) => onChange(updateFunctionality(forms, { software_version_observations: e.currentTarget.value }))} mb="sm" />
            <Group grow mb="sm">
              <Select label={t('inventory.inspection.plugsYes')} data={yesNoOptions} value={functionality.plugs_yes ?? null} onChange={(value) => onChange(updateFunctionality(forms, { plugs_yes: value ?? '' }))} />
              <Select label={t('inventory.inspection.plugsNo')} data={yesNoOptions} value={functionality.plugs_no ?? null} onChange={(value) => onChange(updateFunctionality(forms, { plugs_no: value ?? '' }))} />
            </Group>
            <Textarea label={t('inventory.inspection.plugsObservations')} value={functionality.plugs_observations ?? ''} onChange={(e) => onChange(updateFunctionality(forms, { plugs_observations: e.currentTarget.value }))} mb="sm" />
            <Group grow mb="sm">
              <Select label={t('inventory.inspection.cablesYes')} data={yesNoOptions} value={functionality.cables_yes ?? null} onChange={(value) => onChange(updateFunctionality(forms, { cables_yes: value ?? '' }))} />
              <Select label={t('inventory.inspection.cablesNo')} data={yesNoOptions} value={functionality.cables_no ?? null} onChange={(value) => onChange(updateFunctionality(forms, { cables_no: value ?? '' }))} />
            </Group>
            <Textarea label={t('inventory.inspection.cablesObservations')} value={functionality.cables_observations ?? ''} onChange={(e) => onChange(updateFunctionality(forms, { cables_observations: e.currentTarget.value }))} mb="sm" />
            <Group grow mb="sm">
              <Select label={t('inventory.inspection.fusesYes')} data={yesNoOptions} value={functionality.fuses_yes ?? null} onChange={(value) => onChange(updateFunctionality(forms, { fuses_yes: value ?? '' }))} />
              <Select label={t('inventory.inspection.fusesNo')} data={yesNoOptions} value={functionality.fuses_no ?? null} onChange={(value) => onChange(updateFunctionality(forms, { fuses_no: value ?? '' }))} />
            </Group>
            <Textarea label={t('inventory.inspection.fusesObservations')} value={functionality.fuses_observations ?? ''} onChange={(e) => onChange(updateFunctionality(forms, { fuses_observations: e.currentTarget.value }))} mb="sm" />
            <Textarea label={t('inventory.inspection.otherObservations')} value={functionality.other_observations ?? ''} onChange={(e) => onChange(updateFunctionality(forms, { other_observations: e.currentTarget.value }))} mb="sm" />
            <TextInput label={t('inventory.inspection.signatureLine')} value={functionality.signature_line ?? ''} onChange={(e) => onChange(updateFunctionality(forms, { signature_line: e.currentTarget.value }))} />
          </Accordion.Panel>
        </Accordion.Item>
      ) : null}
    </Accordion>
  );
}
